import React, { useMemo } from 'react';

import CreditsBalanceBanner from '@editor/components/CreditsBalance/CreditsBalanceBanner';
import { FlexColumn } from '@editor/components/utilities/layouts';
import analytics from '@repo/analytics';
import { IconEventWrapper, IconSwitcher } from '@repo/ui/components/Icon';
import { H5 } from '@repo/ui/components/Typography';
import { useTheme } from '@repo/ui/theming';
import noop from 'lodash/noop';

import {
  THEME_SWITCH,
  USER_NAVIGATION_CLICK,
} from '../../../global/analytics/events';
import { useDesignLocks } from '../../../hooks/useDesignLocks';
import { useDesignsStore } from '../../../stores/designsStore';
import MultiOption from '../../MultiOption/MultiOption';
import Switch from '../../Switch/Switch';

import { options as defaultOptions } from './config';
import {
  OptionText,
  IconContainer,
  BottomList,
  BottomListElement,
  Separator,
} from './styles';
import { iconEventWrapperTheme, iconTheme } from './theme';
import { UserNavigationProps } from './types';

/**
 * Menu in which the user can navigate different profile pages.
 */
const UserNavigation: React.FC<UserNavigationProps> = ({
  options: options_ = defaultOptions,
  ...props
}) => {
  const { unlockDesign } = useDesignLocks();

  // This memo function filters Subscription and Referral options from menu on smaller screens until they're mobile optimized
  const options = useMemo(() => {
    let _options = options_;

    const isMobile = window?.matchMedia(
      'only screen and (max-width: 760px)'
    ).matches;

    if (isMobile) {
      const newOptions = Object.entries(_options).filter(
        ([key, _]) => key !== 'Subscription' && key !== 'Referral'
      );
      _options = Object.fromEntries(newOptions);
    }

    return _options;
  }, [options_]);

  const onSelectOption = (optionKey: string): void => {
    analytics.track(USER_NAVIGATION_CLICK, {
      label: options[optionKey].url,
    });
    props.onClickItem(options[optionKey]);
  };

  const handleHelp = (): void => {
    analytics.track(USER_NAVIGATION_CLICK, {
      label: 'help',
    });
    props.onHelp();
  };

  const internalOnLogOut = (): void => {
    const activeDesignId = useDesignsStore.getState().active;
    if (activeDesignId) {
      unlockDesign(activeDesignId);
    }

    if (props.onLogOut) {
      props.onLogOut();
    }
  };

  const renderOption = (optionKey: string): React.ReactNode => {
    const option = options[optionKey];
    return (
      <IconEventWrapper
        fitParent
        activeOnHover={true}
        theme={iconEventWrapperTheme}
      >
        <IconContainer>
          <IconSwitcher
            height="auto"
            icon={option.icon}
            isActive={props.selectedOption === optionKey}
            theme={iconTheme}
          />
        </IconContainer>
        <OptionText>
          <H5 color="unset">
            {props.isTeam ? (option.teamLabel ?? option.label) : option.label}
          </H5>
        </OptionText>
      </IconEventWrapper>
    );
  };

  const { toggleTheme, isDarkTheme, themeDisabled } = useTheme();

  const handleThemeToggle = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const newTheme = toggleTheme();
    analytics.track(THEME_SWITCH, {
      theme: newTheme,
    });
  };

  return (
    <>
      <Separator />
      <FlexColumn gap="8px">
        <MultiOption
          options={options}
          renderOption={renderOption}
          select={onSelectOption}
          selected={props.selectedOption}
        />
        <CreditsBalanceBanner />
      </FlexColumn>
      <BottomList>
        <Separator />
        <BottomListElement
          data-testid="dark-mode-toggle"
          onClick={themeDisabled ? noop : handleThemeToggle}
        >
          <H5 color="unset">Dark Mode</H5>
          <Switch
            checked={isDarkTheme}
            disabled={themeDisabled}
            onChange={noop}
          />
        </BottomListElement>
        <Separator />
        <BottomListElement data-testid="help">
          <H5 color="unset" onClick={(): void => handleHelp()}>
            Help
          </H5>
        </BottomListElement>
        <BottomListElement>
          <H5
            color="unset"
            data-testid="log-out"
            onClick={(): void => internalOnLogOut()}
          >
            Sign Out
          </H5>
        </BottomListElement>
      </BottomList>
    </>
  );
};

export default UserNavigation;
